import { AssetSubClassCode } from '@api/api/entity-constants';
import { type Entity, PrimaryMethod } from '@api/api/entity-schema';
import { PreliminaryPrice } from '@api/api/price-schema';
import { DisplayFormat, SnakeMeta } from '@api/api/timeseries-schema';
import {
  checkSupportedExchange,
  formatPrice,
  getDisplayPriceChange,
  getMarketHours,
} from '@helpers/utils/asset-price/asset-price';
import { LivePriceInfo } from '@terminal/hooks/use-live-price';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import { priceSuffix } from '@terminal/utils/currency/currency';
import { format, isValid } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import i18next, { TFunction } from 'i18next';

import { PriceBase, PriceDisplayReturn } from './price-display.types';

interface MapPriceDisplayProps {
  exchange: string;
  livePriceInfo?: LivePriceInfo;
  preliminaryPrice?: PreliminaryPrice;
  meta?: SnakeMeta;
  isHovered: boolean;
  t: TFunction;
  isObservation?: boolean;
}

export const isPrimaryObservation = (entity: Pick<Entity, 'primary_method'>) =>
  !!entity.primary_method &&
  entity.primary_method !== PrimaryMethod.Price &&
  entity.primary_method !== PrimaryMethod.SpreadPrice;

export const isEconomicSeries = (
  entity: Pick<Entity, 'primary_method' | 'sub_class'>
) =>
  isPrimaryObservation(entity) ||
  [
    AssetSubClassCode.EcoCountry,
    AssetSubClassCode.EcoArea,
    AssetSubClassCode.EcoCentralBank,
    AssetSubClassCode.FiSov,
    AssetSubClassCode.FiInflationSov,
  ].includes(entity.sub_class);

export const getPrimaryObservationUnit = (
  entity: Pick<Entity, 'primary_method'> &
    Partial<Pick<Entity, 'default_snake'>>,
  meta?: SnakeMeta
) => {
  switch (entity.primary_method) {
    case PrimaryMethod.GdpLevel:
      return meta?.snake === entity.default_snake &&
        meta?.display_format === DisplayFormat.PERCENT
        ? '%'
        : '';
    case PrimaryMethod.Yield:
    case PrimaryMethod.CbPolicyRate:
      return '%';
    case PrimaryMethod.PmiNewOrders:
    case PrimaryMethod.Cesi:
    case PrimaryMethod.PriceTwi:
      return '';
    default:
      return priceSuffix(meta?.display_format, meta?.currency);
  }
};

export const getPrimaryObservationLabel = (
  entity: Pick<Entity, 'primary_method' | 'default_snake'>,
  meta: SnakeMeta | undefined
) => {
  // The catalogue uses the same generic yield metadata for bonds, curves and swaps.
  // A primary yield method does not establish yield-to-maturity semantics.
  if (entity.primary_method === PrimaryMethod.Yield) {
    return i18next.t('analyze:analysis.primaryMethod.yield');
  }
  if (
    entity.primary_method === PrimaryMethod.GdpLevel &&
    meta?.snake === entity.default_snake
  ) {
    const method = entity.default_snake.slice(
      entity.default_snake.indexOf('.') + 1
    );
    const labels: Record<string, string> = {
      gdp_yoy_actual: 'gdpAnnualActual',
      gdp_qoq_actual: 'gdpQuarterlyActual',
      gdp_flash_yoy_actual: 'gdpAnnualFlash',
      gdp_flash_qoq_actual: 'gdpQuarterlyFlash',
      'gdp_level.seas_adj.change_pct(horiz=1y)': 'gdpAnnualCalculated',
    };
    if (labels[method]) {
      return i18next.t(`analyze:analysis.primaryMethod.${labels[method]}`);
    }
  }
  const title = meta?.name.english.trim();
  if (title && meta?.snake === entity.default_snake) {
    return title;
  }
  switch (entity.primary_method) {
    case PrimaryMethod.PmiNewOrders:
      return i18next.t('analyze:analysis.primaryMethod.pmiNewOrders');
    case PrimaryMethod.CbPolicyRate:
      return i18next.t('analyze:analysis.primaryMethod.policyRate');
    case PrimaryMethod.Cesi:
      return i18next.t('analyze:analysis.primaryMethod.economicSurpriseIndex');
    case PrimaryMethod.GdpLevel:
      return i18next.t('analyze:analysis.primaryMethod.observation');
    case PrimaryMethod.PriceTwi:
      return i18next.t('analyze:analysis.primaryMethod.tradeWeightedIndex');
    case PrimaryMethod.OptionAdjSpread:
      return i18next.t('analyze:analysis.primaryMethod.optionAdjustedSpread');
    default:
      return i18next.t('analyze:analysis.primaryMethod.observation');
  }
};

const formatObservation = (
  livePriceInfo?: LivePriceInfo,
  meta?: SnakeMeta
): PriceDisplayReturn => {
  const observation =
    livePriceInfo && !livePriceInfo.isLive && livePriceInfo.time
      ? { value: livePriceInfo.price, date: new Date(livePriceInfo.time) }
      : {
          value: meta?.last_value,
          date: new Date((meta?.last_timestamp ?? NaN) / 1_000_000),
        };
  const hasObservationDate =
    isValid(observation.date) && observation.date.getTime() <= Date.now();
  return {
    primary: {
      marketHours: getMarketHours(),
      price:
        Number.isFinite(observation.value) && hasObservationDate
          ? observation.value
          : undefined,
      priceChange: undefined,
      isLive: false,
      isSupported: false,
      asOfDate: hasObservationDate
        ? formatInTimeZone(observation.date, 'UTC', 'PPP')
        : undefined,
    },
  };
};

/**
 * Main formatting function that returns price details for v2 price display
 *
 * Details: https://app.clickup.com/10607188/v/dc/a3pjm-10088/a3pjm-15448
 * @returns primary price and secondary price details
 */
export const formatPriceDisplay = ({
  exchange,
  livePriceInfo,
  preliminaryPrice,
  meta,
  isHovered,
  t,
  isObservation = false,
}: MapPriceDisplayProps): PriceDisplayReturn => {
  if (isObservation) {
    return formatObservation(livePriceInfo, meta);
  }

  const isSupported = checkSupportedExchange(exchange);
  const marketHours = getMarketHours();

  const lastKnownPrice =
    livePriceInfo?.lastKnownPrice !== undefined
      ? livePriceInfo.lastKnownPrice
      : meta?.last_value;
  const price =
    livePriceInfo?.price !== undefined ? livePriceInfo.price : lastKnownPrice;
  const decimals = livePriceInfo?.decimals ?? 2;

  const base: PriceBase = {
    price,
    priceChange: livePriceInfo?.priceChange,
    isLive: livePriceInfo?.isLive,
    marketHours,
    isSupported,
    asOfDate:
      livePriceInfo?.time && isHovered
        ? format(Date.parse(livePriceInfo?.time), 'PPP')
        : undefined,
  };

  const { primaryPrice, secondaryPrice } = formatPrice({
    base,
    meta,
    preliminaryPrice,
    decimals,
    dateLocale: useLanguageStore.getState().languageSetting,
    preMarketLabel: t('analyze:analysis.preMarket'),
    afterHoursLabel: t('analyze:analysis.afterHours'),
  });

  const filterInvalidChangeValues = <T extends PriceBase>(priceBase: T): T => ({
    ...priceBase,
    priceChange: getDisplayPriceChange(priceBase.priceChange, priceBase.isLive),
  });

  const primary = isSupported && !isHovered ? primaryPrice : base;
  const secondary = isSupported ? secondaryPrice : undefined;

  return {
    primary,
    secondary: secondary ? filterInvalidChangeValues(secondary) : undefined,
  };
};

export { getMarketHours };

export type PriceStampType = 'live' | 'notLive' | 'notSupported';

type TypeStampPrice = {
  color: string;
  stampType: PriceStampType;
};

interface GetPriceStampKeyProps {
  isLivePriceSupported: boolean;
  isLive: boolean;
  isDisabled: boolean;
}

export const getPriceStamp = ({
  isLivePriceSupported,
  isLive,
  isDisabled,
}: GetPriceStampKeyProps): TypeStampPrice => {
  const getStampColor = (color: string) =>
    isDisabled ? 'var(--icon-disabled)' : color;

  if (isLivePriceSupported) {
    if (!isLive) {
      return {
        color: getStampColor('var(--icon-disabled)'),
        stampType: 'notLive',
      };
    } else {
      return {
        color: getStampColor('var(--icon-success)'),
        stampType: 'live',
      };
    }
  }

  return {
    color: getStampColor('var(--icon-disabled)'),
    stampType: 'notSupported',
  };
};
